import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import { API_URL } from '../config';

const getAuthHeaders = (contentType = false): HeadersInit => {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Authorization': `Bearer ${token}`
  };
  if (contentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

interface GroupInfo {
  id: number;
  name: string;
  description: string | null;
  invite_code: string | null;
  created_by: number | null;
  created_at: string;
  member_count: number;
  role: string;
  is_active: boolean;
}

interface GroupMember {
  id: number;
  email: string;
  name: string | null;
  role: string;
  joined_at: string;
}

const FEED_TYPES: Array<{key: string; label: string}> = [
  { key: 'demo', label: 'DJI Demo Flight' },
  { key: 'rtmp', label: 'RTMP / OBS' },
  { key: 'dji', label: 'DJI Live' },
  { key: 'hls', label: 'DJI Livestream (HLS)' },
  { key: 'phone', label: 'Phone Sensors' },
];

interface GroupHomeView {
  lat: number;
  lng: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
}

interface GroupPanelProps {
  onGroupChange: (homeView?: GroupHomeView) => void;
  initialGroupId?: number | null;
}

export function GroupPanel({ onGroupChange, initialGroupId }: GroupPanelProps) {
  const { confirm } = useConfirm();
  const { user, refreshUser } = useAuth();
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(initialGroupId ?? null);

  useEffect(() => {
    if (initialGroupId) setSelectedGroupId(initialGroupId);
  }, [initialGroupId]);

  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'create' | 'join'>('list');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [feedTypes, setFeedTypes] = useState<string[]>([]);
  const [allAccess, setAllAccess] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedSaving, setFeedSaving] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: number; email: string; name: string | null }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
        if (!selectedGroupId && data.active_group_id) {
          setSelectedGroupId(data.active_group_id);
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId]);

  const fetchMembers = useCallback(async (groupId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}/members`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setMembers(await response.json());
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchFeeds = useCallback(async (groupId: number) => {
    setFeedLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}/feeds`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFeedTypes(data.feed_types || []);
        setAllAccess(data.all_access);
      }
    } catch {
      // not authorized or error — hide feed section
      setFeedTypes([]);
      setAllAccess(true);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  const saveFeeds = useCallback(async (groupId: number, types: string[], isAllAccess: boolean) => {
    setFeedSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}/feeds`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ feed_types: types, all_access: isAllAccess })
      });
      if (response.ok) {
        const data = await response.json();
        setFeedTypes(data.feed_types || []);
        setAllAccess(data.all_access);
      }
    } catch {
      // ignore
    } finally {
      setFeedSaving(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups, user?.group_id]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchMembers(selectedGroupId);
      fetchFeeds(selectedGroupId);
    } else {
      setMembers([]);
    }
  }, [selectedGroupId, fetchMembers, fetchFeeds]);

  const handleCreate = async () => {
    if (!newGroupName.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ name: newGroupName.trim(), description: newGroupDesc.trim() || null })
      });
      if (response.ok) {
        const created = await response.json();
        setNewGroupName('');
        setNewGroupDesc('');
        setView('list');
        setSelectedGroupId(created.id);
        await refreshUser();
        await fetchGroups();
        onGroupChange();
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to create group');
      }
    } catch {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!inviteCode.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/groups/join`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ invite_code: inviteCode.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        setInviteCode('');
        setView('list');
        setSelectedGroupId(data.group_id);
        await refreshUser();
        await fetchGroups();
        onGroupChange();
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to join group');
      }
    } catch {
      setError('Network error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwitch = async (groupId: number) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/switch`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ group_id: groupId })
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedGroupId(groupId);
        await refreshUser();
        await fetchGroups();
        onGroupChange(data.home_view || undefined);
      }
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async (groupId: number, groupName: string) => {
    if (!await confirm({ message: `Leave "${groupName}"? You will lose access to its data.`, confirmLabel: 'Leave' })) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/leave`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ group_id: groupId })
      });
      if (response.ok) {
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
        await refreshUser();
        await fetchGroups();
        onGroupChange();
      }
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const searchUsersForGroup = useCallback(async (query: string, groupId: number) => {
    if (query.length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/groups/${groupId}/search-users?q=${encodeURIComponent(query)}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        setSearchResults(await response.json());
      }
    } catch { /* ignore */ }
    finally { setSearchLoading(false); }
  }, []);

  const handleMemberSearchChange = (value: string, groupId: number) => {
    setMemberSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length >= 2) {
      searchTimerRef.current = setTimeout(() => searchUsersForGroup(value, groupId), 300);
    } else {
      setSearchResults([]);
    }
  };

  const handleAddMember = async (userId: number, groupId: number) => {
    setAddingMember(true);
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        setMemberSearch('');
        setSearchResults([]);
        setShowAddMember(false);
        await fetchMembers(groupId);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to add member');
      }
    } catch { setError('Network error'); }
    finally { setAddingMember(false); }
  };

  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;

  const canManageFeeds = selectedGroup && (
    user?.is_admin ||
    selectedGroup.created_by === user?.id ||
    selectedGroup.role === 'admin'
  );

  if (loading) {
    return <div className="group-panel"><div className="group-loading">Loading...</div></div>;
  }

  return (
    <div className="group-panel">
      {error && <div className="group-error">{error}</div>}

      {view === 'list' && (
        <>
          {!selectedGroup && (
            <div className="no-group-message">
              You are not in any group. Create or join one to start collaborating.
            </div>
          )}

          {selectedGroup && (
            <div className="group-detail">
              {selectedGroup.description && <div className="group-description">{selectedGroup.description}</div>}
              {selectedGroup.invite_code && (
                <div className="group-invite">
                  <label>Invite Code:</label>
                  <div className="invite-code-row">
                    <code className="invite-code">{selectedGroup.invite_code}</code>
                    <button className="copy-btn" onClick={() => copyInviteCode(selectedGroup.invite_code!)}>
                      {copiedCode ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
              <div className="group-members">
                <label>Members ({members.length}):</label>
                <div className="members-list">
                  {members.map(m => (
                    <div key={m.id} className="member-item">
                      <span>{m.name || m.email}</span>
                      <span className={`role-badge role-${m.role}`}>{m.role}</span>
                    </div>
                  ))}
                </div>
                {canManageFeeds && (
                  <div className="group-add-member">
                    <button className="group-action-btn add-member-toggle" onClick={() => { setShowAddMember(!showAddMember); setMemberSearch(''); setSearchResults([]); }}>
                      {showAddMember ? 'Cancel' : '+ Add Member'}
                    </button>
                    {showAddMember && (
                      <div className="add-member-search">
                        <input
                          type="text"
                          placeholder="Search by email or name..."
                          className="add-member-input"
                          value={memberSearch}
                          onChange={(e) => handleMemberSearchChange(e.target.value, selectedGroup.id)}
                          autoFocus
                        />
                        {searchLoading && <div className="search-loading">Searching...</div>}
                        {searchResults.length > 0 && (
                          <div className="search-results">
                            {searchResults.map(u => (
                              <div key={u.id} className="search-result-item">
                                <div className="search-result-info">
                                  <span className="search-result-name">{u.name || u.email}</span>
                                  {u.name && <span className="search-result-email">{u.email}</span>}
                                </div>
                                <button className="search-result-add" disabled={addingMember} onClick={() => handleAddMember(u.id, selectedGroup.id)}>
                                  Add
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {memberSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
                          <div className="no-results">No users found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {canManageFeeds && !feedLoading && (
                <div className="group-feed-access">
                  <label>Video Feed Access:</label>
                  <div className="feed-all-toggle">
                    <label className="feed-checkbox">
                      <input
                        type="checkbox"
                        checked={allAccess}
                        disabled={feedSaving}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAllAccess(true);
                            setFeedTypes([]);
                            saveFeeds(selectedGroup.id, [], true);
                          } else {
                            setAllAccess(false);
                            const all = FEED_TYPES.map(f => f.key);
                            setFeedTypes(all);
                            saveFeeds(selectedGroup.id, all, false);
                          }
                        }}
                      />
                      All feeds (unrestricted)
                    </label>
                  </div>
                  {!allAccess && (
                    <div className="feed-type-list">
                      {FEED_TYPES.map(ft => (
                        <label key={ft.key} className="feed-checkbox">
                          <input
                            type="checkbox"
                            checked={feedTypes.includes(ft.key)}
                            disabled={feedSaving}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...feedTypes, ft.key]
                                : feedTypes.filter(t => t !== ft.key);
                              setFeedTypes(updated);
                              saveFeeds(selectedGroup.id, updated, false);
                            }}
                          />
                          {ft.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="group-actions">
            <button className="group-action-btn create" onClick={() => { setView('create'); setError(''); }}>
              Create Group
            </button>
            <button className="group-action-btn join" onClick={() => { setView('join'); setError(''); }}>
              Join Group
            </button>
          </div>
        </>
      )}

      {view === 'create' && (
        <div className="group-form">
          <input
            type="text"
            placeholder="Group name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            maxLength={255}
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
            maxLength={1000}
          />
          <div className="group-form-actions">
            <button onClick={() => { setView('list'); setError(''); }} disabled={actionLoading}>Cancel</button>
            <button className="save-btn" onClick={handleCreate} disabled={actionLoading || !newGroupName.trim()}>
              {actionLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {view === 'join' && (
        <div className="group-form">
          <input
            type="text"
            placeholder="Enter invite code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            maxLength={64}
          />
          <div className="group-form-actions">
            <button onClick={() => { setView('list'); setError(''); }} disabled={actionLoading}>Cancel</button>
            <button className="save-btn" onClick={handleJoin} disabled={actionLoading || !inviteCode.trim()}>
              {actionLoading ? 'Joining...' : 'Join'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
